import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { COMPANY_ROLE_CONFIGS, COMPANY_CONFIGS } from "../../../mocks/companyTypes";

const ALL_ROLES = Object.entries(COMPANY_ROLE_CONFIGS).flatMap(([companyType, roles]) =>
  roles.map(r => ({ key: r.key, label: r.label, company: COMPANY_CONFIGS[companyType as keyof typeof COMPANY_CONFIGS].label }))
);
// 중복 제거
const UNIQUE_ROLES = Array.from(
  new Map(ALL_ROLES.map(r => [r.key, r])).values()
);

interface StudentInfo {
  userId: string;
  email: string;
  jobRole: string;
  joinedAt: string;
  capital: number;
  reputation: number;
  creditScore: number;
  taskCount: number;
  rank: string;
}

interface TaskCompletion {
  id: string;
  task_id: string;
  hub: string;
  completed_at: string;
}

interface Props {
  student: StudentInfo | null;
  workspaceId: string;
  onClose: () => void;
}

function getRankColor(rep: number) {
  if (rep >= 81) return "text-yellow-500 bg-yellow-400/10 border-yellow-400/25";
  if (rep >= 61) return "text-[#E8375F] bg-[#E8375F]/10 border-[#E8375F]/25";
  if (rep >= 41) return "text-[#FF9F43] bg-[#FF9F43]/10 border-[#FF9F43]/25";
  if (rep >= 21) return "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/25";
  return "text-[#8B91A8] bg-[#8B91A8]/10 border-[#8B91A8]/25";
}

const HUB_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  "foundation": { label: "창업 기초", icon: "ri-building-line", color: "#7A9BFF" },
  "ar": { label: "아티스트 개발", icon: "ri-music-2-line", color: "#FF9F43" },
  "campaign": { label: "캠페인", icon: "ri-megaphone-line", color: "#E8375F" },
  "concert": { label: "콘서트", icon: "ri-live-line", color: "#00C896" },
  "ip": { label: "IP 관리", icon: "ri-copyright-line", color: "#A78BFA" },
};

function getHubInfo(hub: string) {
  const key = hub?.toLowerCase().replace(/-hub$/, "").replace(/hub$/, "").trim();
  return HUB_LABELS[key] ?? { label: hub ?? "기타", icon: "ri-task-line", color: "#5A5E72" };
}

export default function StudentDetailModal({ student, workspaceId, onClose }: Props) {
  const [completions, setCompletions] = useState<TaskCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"progress" | "role">("progress");

  // 직무 관리 상태
  const [selectedRole, setSelectedRole] = useState(student?.jobRole ?? "CEO");
  const [roleLocked, setRoleLocked] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [roleSaved, setRoleSaved] = useState(false);

  const fetchCompletions = useCallback(async () => {
    if (!student || !workspaceId) return;
    setLoading(true);
    const { data } = await supabase
      .from("task_completions")
      .select("id, task_id, hub, completed_at")
      .eq("workspace_id", workspaceId)
      .eq("user_id", student.userId)
      .order("completed_at", { ascending: false });
    setCompletions(data ?? []);
    setLoading(false);
  }, [student, workspaceId]);

  const fetchRoleLock = useCallback(async () => {
    if (!student || !workspaceId) return;
    const { data } = await supabase
      .from("workspace_members")
      .select("job_role, role_locked")
      .eq("workspace_id", workspaceId)
      .eq("user_id", student.userId)
      .maybeSingle();
    if (data) {
      setSelectedRole(data.job_role ?? "CEO");
      setRoleLocked(data.role_locked ?? false);
    }
  }, [student, workspaceId]);

  useEffect(() => {
    fetchCompletions();
    fetchRoleLock();
  }, [fetchCompletions, fetchRoleLock]);

  const handleSaveRole = async () => {
    if (!student || !workspaceId) return;
    setSavingRole(true);
    // workspace_members 업데이트
    await supabase.from("workspace_members").update({
      job_role: selectedRole,
      role_locked: roleLocked,
    }).eq("workspace_id", workspaceId).eq("user_id", student.userId);
    // profiles.job_role 동기화
    await supabase.from("profiles").update({ job_role: selectedRole })
      .eq("id", student.userId);
    setSavingRole(false);
    setRoleSaved(true);
    setTimeout(() => setRoleSaved(false), 2500);
  };

  if (!student) return null;

  // 허브별 그룹핑
  const grouped = completions.reduce<Record<string, TaskCompletion[]>>((acc, c) => {
    const hub = c.hub ?? "unknown";
    if (!acc[hub]) acc[hub] = [];
    acc[hub].push(c);
    return acc;
  }, {});

  const hubKeys = Object.keys(grouped);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl bg-[#0F1015] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        {/* 헤더 */}
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1C1F2E] border border-white/[0.1] flex items-center justify-center text-[#888888] text-sm font-bold shrink-0">
              {student.email[0]?.toUpperCase() ?? "?"}
            </div>
            <div>
              <div className="text-[#F2F2F2] text-sm font-semibold truncate max-w-xs">{student.email}</div>
              <div className="text-[#555555] text-xs mt-0.5 flex items-center gap-2">
                <span>{student.jobRole !== "-" ? student.jobRole : "직무 미선택"}</span>
                <span className="text-[#333333]">·</span>
                <span>
                  {new Date(student.joinedAt).toLocaleDateString("ko-KR", { month: "long", day: "numeric" })} 입장
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-[#555555] hover:text-white hover:bg-white/[0.06] transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* 스탯 요약 */}
        <div className="px-6 py-4 grid grid-cols-4 gap-3 shrink-0 border-b border-white/[0.05]">
          {[
            {
              label: "직급",
              value: student.rank,
              sub: `${student.reputation}점`,
              badge: true,
              rep: student.reputation,
            },
            {
              label: "직급 점수",
              value: `${student.reputation}점`,
              sub: `다음 직급까지 ${student.reputation >= 80 ? "최고!" : `${(Math.ceil(student.reputation / 20) * 20) - student.reputation}점`}`,
              badge: false,
              rep: student.reputation,
            },
            {
              label: "자본금",
              value: `${student.capital.toLocaleString()}만원`,
              sub: "",
              badge: false,
              rep: -1,
            },
            {
              label: "완료 태스크",
              value: `${student.taskCount}건`,
              sub: `${hubKeys.length}개 허브`,
              badge: false,
              rep: -1,
            },
          ].map((item, i) => (
            <div key={i} className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl px-3 py-3">
              <div className="text-[#3E4155] text-[10px] uppercase tracking-widest mb-1.5">{item.label}</div>
              {item.badge ? (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${getRankColor(item.rep)}`}>
                  {item.value}
                </span>
              ) : (
                <div className="text-[#CCCCCC] text-sm font-bold">{item.value}</div>
              )}
              {item.sub && (
                <div className="text-[#3E4155] text-[10px] mt-1">{item.sub}</div>
              )}
            </div>
          ))}
        </div>

        {/* 직급 프로그레스 바 */}
        <div className="px-6 py-3 shrink-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[#3E4155] text-[10px] uppercase tracking-widest">직급 경력 점수</span>
            <span className="text-[#555555] text-[10px]">{student.reputation} / 100</span>
          </div>
          <div className="w-full h-1.5 bg-[#1C1F2E] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${student.reputation}%`,
                background: student.reputation >= 81
                  ? "linear-gradient(90deg, #EAB308, #F59E0B)"
                  : student.reputation >= 61
                  ? "linear-gradient(90deg, #E8375F, #F76F8B)"
                  : student.reputation >= 41
                  ? "linear-gradient(90deg, #FF9F43, #FFBF70)"
                  : student.reputation >= 21
                  ? "linear-gradient(90deg, #4B6CF7, #7A9BFF)"
                  : "linear-gradient(90deg, #5A5E72, #8B91A8)",
              }}
            />
          </div>
        </div>

        {/* 탭 */}
        <div className="px-6 pt-3 pb-0 flex gap-1 shrink-0 border-b border-white/[0.05]">
          {([["progress", "학습 진행 현황", "ri-bar-chart-line"], ["role", "직무 관리", "ri-settings-3-line"]] as const).map(([tab, label, icon]) => (
            <button key={tab} type="button" onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap -mb-px ${
                activeTab === tab
                  ? "border-[#E8375F] text-[#E8375F]"
                  : "border-transparent text-[#3E4155] hover:text-[#888888]"
              }`}>
              <i className={`${icon} text-sm`}></i>{label}
            </button>
          ))}
        </div>

        {/* 직무 관리 탭 */}
        {activeTab === "role" && (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* 현재 직무 */}
            <div className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl p-4">
              <div className="text-[#3E4155] text-[10px] uppercase tracking-widest mb-3">직무 변경 (강제 지정)</div>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
                {UNIQUE_ROLES.map(r => (
                  <button key={r.key} type="button" onClick={() => setSelectedRole(r.key)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                      selectedRole === r.key
                        ? "border-[#E8375F]/40 bg-[#E8375F]/[0.08] text-[#E8375F]"
                        : "border-white/[0.05] text-[#888888] hover:border-white/10 hover:text-[#CCCCCC] bg-white/[0.01]"
                    }`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{r.label}</div>
                    </div>
                    {selectedRole === r.key && <i className="ri-checkbox-circle-fill text-[#E8375F] text-sm shrink-0"></i>}
                  </button>
                ))}
              </div>
            </div>

            {/* 직무 잠금 토글 */}
            <div className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[#CCCCCC] text-sm font-semibold">직무 변경 잠금</div>
                  <div className="text-[#3E4155] text-xs mt-0.5">
                    {roleLocked
                      ? "잠금 ON — 학생이 직무를 변경할 수 없어요"
                      : "잠금 OFF — 학생이 직접 직무를 바꿀 수 있어요"}
                  </div>
                </div>
                <button type="button" onClick={() => setRoleLocked(v => !v)}
                  className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${roleLocked ? "bg-[#E8375F]" : "bg-[#252830] border border-white/10"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow ${roleLocked ? "translate-x-6" : "translate-x-0"}`} />
                </button>
              </div>
              {roleLocked && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-[#E8375F]/[0.06] border border-[#E8375F]/20 rounded-lg">
                  <i className="ri-lock-line text-[#E8375F] text-sm mt-0.5 shrink-0"></i>
                  <p className="text-[#E8375F]/80 text-[10px] leading-relaxed">
                    잠금 시 학생의 직무 선택 화면이 비활성화됩니다.<br />
                    강제 지정한 직무로 고정되며 학생이 스스로 변경할 수 없어요.
                  </p>
                </div>
              )}
            </div>

            {/* 저장 버튼 */}
            <button type="button" onClick={handleSaveRole} disabled={savingRole}
              className={`w-full py-3 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                roleSaved ? "bg-emerald-500 text-white" : "bg-[#E8375F] text-white hover:bg-[#FF4B7A]"
              }`}>
              {savingRole
                ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>저장 중...</span>
                : roleSaved
                  ? <span className="flex items-center justify-center gap-2"><i className="ri-checkbox-circle-fill"></i>저장 완료! (다음 로그인 시 반영)</span>
                  : <span className="flex items-center justify-center gap-2"><i className="ri-save-line"></i>직무 설정 저장</span>
              }
            </button>
          </div>
        )}

        {/* 태스크 완료 내역 탭 */}
        {activeTab === "progress" && (
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="text-[#3E4155] text-[10px] uppercase tracking-widest mb-3 pt-4">
            태스크 완료 내역
          </div>

          {loading ? (
            <div className="py-12 text-center text-[#3E4155] text-sm animate-pulse">
              내역 로딩 중...
            </div>
          ) : completions.length === 0 ? (
            <div className="py-12 text-center">
              <i className="ri-checkbox-blank-circle-line text-3xl text-[#252830] block mb-3"></i>
              <p className="text-[#3E4155] text-sm">아직 완료한 태스크가 없어요</p>
            </div>
          ) : (
            <div className="space-y-4">
              {hubKeys.map(hubKey => {
                const items = grouped[hubKey];
                const hubInfo = getHubInfo(hubKey);
                return (
                  <div key={hubKey} className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl overflow-hidden">
                    {/* 허브 헤더 */}
                    <div className="px-4 py-2.5 flex items-center justify-between border-b border-[#252830]">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 flex items-center justify-center rounded"
                          style={{ backgroundColor: `${hubInfo.color}15` }}
                        >
                          <i className={`${hubInfo.icon} text-xs`} style={{ color: hubInfo.color }}></i>
                        </div>
                        <span className="text-[#CCCCCC] text-xs font-semibold">{hubInfo.label}</span>
                      </div>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{
                          color: hubInfo.color,
                          backgroundColor: `${hubInfo.color}15`,
                        }}
                      >
                        {items.length}건 완료
                      </span>
                    </div>

                    {/* 태스크 목록 */}
                    <div className="divide-y divide-[#1E2130]">
                      {items.map(item => (
                        <div key={item.id} className="px-4 py-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <i className="ri-checkbox-circle-fill text-emerald-400/60 text-sm shrink-0"></i>
                            <span className="text-[#888888] text-xs font-mono">{item.task_id}</span>
                          </div>
                          <span className="text-[#3E4155] text-[10px] tabular-nums">
                            {new Date(item.completed_at).toLocaleString("ko-KR", {
                              month: "numeric",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
